import { useState, useMemo, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Images, RefreshCw, Check, AlertTriangle, Ban, Search, X, Sparkles, Upload } from "lucide-react";

/**
 * Review-and-approve UI for stock image matching.
 *
 * The matcher only ever PROPOSES. Nothing is written until the reviewer ticks
 * rows and confirms, and the default selection is HIGH-confidence rows only —
 * REVIEW and NONE start unticked so an impatient "Apply" cannot silently put a
 * plausible-but-wrong photo on a live customer menu.
 *
 * Every row can be corrected in place: the runner-up chips swap to an
 * alternative the matcher considered, and "Browse" opens the whole library so
 * a reviewer can hand-pick — including for rows the matcher gave up on.
 *
 * "Generate" fills the gaps the library cannot: it asks the server for AI
 * drafts in the house style (BRIEF-ai-image-gen-stock-library.md). "Upload"
 * takes a file from disk through the same path. Drafts are inactive
 * stock_images rows — invisible to Browse and to the matcher — and only an
 * APPLIED draft becomes a library row. Every draft generated in this
 * dialog session that is not applied is discarded when the dialog closes, so
 * nothing unreviewed ever lands in the library.
 */

type Confidence = "HIGH" | "REVIEW" | "NONE";

interface Proposal {
  productId: number;
  productName: string;
  stockImageId: number | null;
  score: number;
  confidence: Confidence;
  reason: string;
  alternatives: Array<{ stockImageId: number; slug: string; score: number }>;
  productCategory: string | null;
  /** Deterministic prefill for the Generate panel; the reviewer edits it. */
  suggestedSubject: string;
}

interface MatchResponse {
  orgSlug: string;
  totalProducts: number;
  skippedWithExistingImage: number;
  counts: Record<Confidence, number>;
  librarySize: number;
  /** False when the server has no OPENAI_API_KEY — Generate is shown disabled. */
  generationAvailable: boolean;
  generationModel: string | null;
  proposals: Proposal[];
}

interface Draft { stockImageId: number; slug: string }

interface GenerateResponse {
  drafts: Draft[];
  prompt: string;
  model: string;
  referenceCategory: string;
  ms: number;
}

// One image per call; "Try again" is the second opinion. Each call also sends
// the reference images, which are most of its cost, so more variants per call
// is cheaper per image — but a single good one is usually all a row needs.
const GENERATE_VARIANTS = 1;
/**
 * Calls in flight at once. Each takes ~20 s, so a reviewer who has to wait for
 * one before starting the next spends the session watching a spinner. Three
 * keeps the per-org spend visible (a runaway would be 3x, not Nx) and stays
 * well inside the provider's concurrency. Batch mode uses the same pool.
 */
const MAX_CONCURRENT = 3;

interface StockImageMeta {
  id: number;
  section: string;
  category: string;
  slug: string;
  name: string;
  verified: boolean;
}

const BANDS: Array<{
  key: Confidence; label: string; blurb: string;
  Icon: typeof Check; tone: string;
}> = [
  {
    key: "HIGH", label: "Confident", Icon: Check, tone: "text-green-700 bg-green-50 border-green-200",
    blurb: "Unambiguous match. Selected by default.",
  },
  {
    key: "REVIEW", label: "Needs a look", Icon: AlertTriangle, tone: "text-amber-700 bg-amber-50 border-amber-200",
    blurb: "A close alternative exists, the pack size differs, or the image's own name is unconfirmed. Check the picture before ticking.",
  },
  {
    key: "NONE", label: "No match", Icon: Ban, tone: "text-gray-600 bg-gray-50 border-gray-200",
    blurb: "Nothing in the library fits by name. Browse to pick one by hand, generate one in the house style, or leave the placeholder.",
  },
];

const thumbUrl = (id: number) => `/api/super-admin/stock-images/${id}/thumb`;

export function StockImageMatchDialog({
  orgId, open, onOpenChange,
}: {
  orgId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [result, setResult] = useState<MatchResponse | null>(null);
  const [includeWithImages, setIncludeWithImages] = useState(false);
  // productId -> chosen stockImageId. Absent = not being applied.
  const [selected, setSelected] = useState<Record<number, number>>({});
  // productId currently browsing the full library, if any.
  const [browsing, setBrowsing] = useState<number | null>(null);

  // ── Generation state ──
  // productIds whose Generate panel is open. Several can be open at once, so
  // a reviewer can start one row and move on to the next while it cooks.
  const [genPanels, setGenPanels] = useState<Record<number, true>>({});
  // productId -> drafts made for it this session (generated or uploaded, in order).
  const [drafts, setDrafts] = useState<Record<number, Draft[]>>({});
  // productId -> uploaded draft ids, so the badge can say "uploaded" not "generated".
  const [uploadedIds, setUploadedIds] = useState<Record<number, true>>({});
  // productId with a file upload in flight.
  const [uploading, setUploading] = useState<Record<number, true>>({});
  const fileInputs = useRef<Record<number, HTMLInputElement | null>>({});
  // productId -> edited subject line (absent = use the server's suggestion).
  const [subjects, setSubjects] = useState<Record<number, string>>({});
  // productId -> false when the reviewer unticked "Keep in library".
  const [keepInLibrary, setKeepInLibrary] = useState<Record<number, boolean>>({});
  // productIds with a generate call in flight — at most MAX_CONCURRENT. The
  // ref mirrors the state for the async loops, which can't read fresh state.
  const [generating, setGenerating] = useState<Record<number, true>>({});
  const inflightRef = useRef<Set<number>>(new Set());
  // Batch progress for "Generate for all".
  const [batch, setBatch] = useState<{ done: number; total: number } | null>(null);
  const batchCancel = useRef(false);
  // Set when the dialog closes so a call that lands afterwards discards its
  // own drafts instead of leaving them for the nightly sweep.
  const closedRef = useRef(false);
  // Every draft id minted this session. On close, whatever was not applied is
  // discarded — a ref so the close handler always sees the latest set.
  const sessionDraftIds = useRef<Set<number>>(new Set());

  // The whole produce library (metadata only — thumbnails load per-id).
  // Fetched lazily the first time someone opens Browse.
  const library = useQuery<StockImageMeta[]>({
    queryKey: ["/api/super-admin/stock-images", "produce"],
    queryFn: () => superAdminGet("/api/super-admin/stock-images?section=produce"),
    enabled: browsing !== null,
    staleTime: 5 * 60 * 1000,
  });
  const libraryById = useMemo(
    () => Object.fromEntries((library.data ?? []).map((s) => [s.id, s])),
    [library.data],
  );

  const matchMutation = useMutation<MatchResponse, Error, { includeWithImages: boolean }>({
    mutationFn: (opts) =>
      superAdminPost(`/api/super-admin/organizations/${orgId}/stock-images/match`, opts),
    onSuccess: (data) => {
      setResult(data);
      // Pre-tick only the confident rows.
      const initial: Record<number, number> = {};
      for (const p of data.proposals) {
        if (p.confidence === "HIGH" && p.stockImageId) initial[p.productId] = p.stockImageId;
      }
      setSelected(initial);
      setBrowsing(null);
    },
    onError: (e) =>
      toast({ title: "Matching failed", description: e.message, variant: "destructive" }),
  });

  const isDraftId = (id: number) => sessionDraftIds.current.has(id);

  /**
   * Delete every draft minted this session except `keep`. Fire-and-forget:
   * the server sweeps anything that slips through after 24 h, and the call
   * refuses to touch anything that is not a draft.
   */
  const discardUnappliedDrafts = (keep: Set<number> = new Set()) => {
    const ids = Array.from(sessionDraftIds.current).filter((id) => !keep.has(id));
    sessionDraftIds.current = new Set();
    if (ids.length === 0) return;
    superAdminPost("/api/super-admin/stock-images/drafts/discard", { ids }).catch(() => {
      /* the nightly sweep is the backstop */
    });
  };

  const applyMutation = useMutation({
    mutationFn: (assignments: Array<{ productId: number; stockImageId: number; keepInLibrary?: boolean }>) =>
      superAdminPost(`/api/super-admin/organizations/${orgId}/stock-images/apply`, { assignments }),
    onSuccess: (data: any, assignments) => {
      queryClient.invalidateQueries({ queryKey: ['/api/super-admin/organizations', orgId, 'products'] });
      // Applied drafts are library rows now (or deliberately deleted one-offs);
      // everything else generated this session goes.
      discardUnappliedDrafts(new Set(assignments.map((a) => a.stockImageId)));
      const bits = [`${data.applied} product${data.applied === 1 ? "" : "s"} updated`];
      if (data.promoted) bits.push(`${data.promoted} generated image${data.promoted === 1 ? "" : "s"} added to the library`);
      if (data.failed?.length) bits.push(`${data.failed.length} failed`);
      toast({ title: "Images applied", description: bits.join(", ") });
      onOpenChange(false);
      setResult(null);
      setSelected({});
    },
    onError: (e: any) =>
      toast({ title: "Apply failed", description: e.message, variant: "destructive" }),
  });

  const setInflight = (productId: number, on: boolean) => {
    if (on) inflightRef.current.add(productId); else inflightRef.current.delete(productId);
    setGenerating(Object.fromEntries(Array.from(inflightRef.current).map((id) => [id, true as const])));
  };
  const inflightCount = Object.keys(generating).length;
  const atCap = inflightCount >= MAX_CONCURRENT;

  /**
   * One product, one call. Returns the new drafts or null on failure. Safe to
   * call for several products at once, up to MAX_CONCURRENT; a call for a row
   * that is already in flight, or over the cap, is a no-op.
   */
  const generateFor = async (p: Proposal, subject: string): Promise<Draft[] | null> => {
    if (inflightRef.current.has(p.productId) || inflightRef.current.size >= MAX_CONCURRENT) return null;
    setInflight(p.productId, true);
    try {
      const r: GenerateResponse = await superAdminPost(
        `/api/super-admin/organizations/${orgId}/stock-images/generate`,
        { productId: p.productId, subject, variants: GENERATE_VARIANTS },
      );
      if (closedRef.current) {
        // The dialog closed while this was cooking: nobody will review these.
        superAdminPost("/api/super-admin/stock-images/drafts/discard", {
          ids: r.drafts.map((d) => d.stockImageId),
        }).catch(() => { /* nightly sweep */ });
        return null;
      }
      for (const d of r.drafts) sessionDraftIds.current.add(d.stockImageId);
      setDrafts((prev) => ({ ...prev, [p.productId]: [...(prev[p.productId] ?? []), ...r.drafts] }));
      return r.drafts;
    } catch (e: any) {
      if (!closedRef.current) {
        toast({ title: `Generation failed for ${p.productName}`, description: e.message, variant: "destructive" });
      }
      return null;
    } finally {
      setInflight(p.productId, false);
    }
  };

  const subjectFor = (p: Proposal) => subjects[p.productId] ?? p.suggestedSubject;

  /**
   * A small worker pool over the band, never Promise.all over the rows: each
   * worker takes the next row off a shared queue, so results land row by row,
   * one failure doesn't abandon the rest, and Cancel stops workers picking up
   * new rows (calls already in flight finish). Rows already cooking are
   * skipped. Nothing is pre-ticked — the reviewer still picks.
   */
  const generateForBand = async (rows: Proposal[]) => {
    batchCancel.current = false;
    const queue = rows.filter((p) => !inflightRef.current.has(p.productId));
    const total = queue.length;
    let done = 0;
    setBatch({ done, total });
    const worker = async () => {
      while (queue.length > 0 && !batchCancel.current) {
        const p = queue.shift()!;
        await generateFor(p, subjectFor(p));
        done++;
        setBatch({ done, total });
      }
    };
    const free = Math.max(1, MAX_CONCURRENT - inflightRef.current.size);
    await Promise.all(Array.from({ length: Math.min(free, total) }, worker));
    setBatch(null);
  };

  /**
   * Manual upload through the same review path as generation: the file becomes
   * a draft on the row and is pre-selected (the reviewer chose it deliberately),
   * but nothing reaches the product until Apply.
   */
  const uploadFor = async (p: Proposal, file: File) => {
    setUploading((prev) => ({ ...prev, [p.productId]: true }));
    try {
      const fd = new FormData();
      fd.append("productId", String(p.productId));
      fd.append("image", file);
      const r: { draft: Draft } = await superAdminUpload(
        `/api/super-admin/organizations/${orgId}/stock-images/upload`, fd,
      );
      if (closedRef.current) {
        superAdminPost("/api/super-admin/stock-images/drafts/discard", { ids: [r.draft.stockImageId] }).catch(() => {});
        return;
      }
      sessionDraftIds.current.add(r.draft.stockImageId);
      setUploadedIds((prev) => ({ ...prev, [r.draft.stockImageId]: true }));
      setDrafts((prev) => ({ ...prev, [p.productId]: [...(prev[p.productId] ?? []), r.draft] }));
      setSelected((prev) => ({ ...prev, [p.productId]: r.draft.stockImageId }));
    } catch (e: any) {
      if (!closedRef.current) {
        toast({ title: `Upload failed for ${p.productName}`, description: e.message, variant: "destructive" });
      }
    } finally {
      setUploading((prev) => { const next = { ...prev }; delete next[p.productId]; return next; });
    }
  };

  const togglePanel = (productId: number) =>
    setGenPanels((prev) => {
      const next = { ...prev };
      if (next[productId]) delete next[productId]; else next[productId] = true;
      return next;
    });

  const handleOpenChange = (o: boolean) => {
    if (!o) {
      batchCancel.current = true;
      closedRef.current = true;
      discardUnappliedDrafts();
    }
    onOpenChange(o);
  };

  // Run the scan as soon as the dialog opens; re-run when the scope toggles.
  useEffect(() => {
    if (open) { closedRef.current = false; matchMutation.mutate({ includeWithImages }); }
    else {
      setResult(null); setSelected({}); setBrowsing(null);
      setGenPanels({}); setDrafts({}); setSubjects({}); setKeepInLibrary({}); setBatch(null);
      setUploadedIds({}); setUploading({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, includeWithImages]);

  const grouped = useMemo(() => {
    const g: Record<Confidence, Proposal[]> = { HIGH: [], REVIEW: [], NONE: [] };
    for (const p of result?.proposals ?? []) g[p.confidence].push(p);
    return g;
  }, [result]);

  const selectedCount = Object.keys(selected).length;

  const choose = (productId: number, imageId: number | null) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (imageId === null || next[productId] === imageId) delete next[productId];
      else next[productId] = imageId;
      return next;
    });
  };

  const selectBand = (band: Confidence, on: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      for (const p of grouped[band]) {
        if (on && p.stockImageId) next[p.productId] = p.stockImageId;
        else delete next[p.productId];
      }
      return next;
    });
  };

  // Label for whatever is currently chosen on a row, whether proposal,
  // alternative, or a hand-pick from the library.
  const chosenLabel = (p: Proposal) => {
    const id = selected[p.productId];
    if (!id) return null;
    if (id === p.stockImageId) return null;
    const alt = p.alternatives.find((a) => a.stockImageId === id);
    if (alt) return { text: alt.slug, kind: "alt" as const };
    const draft = (drafts[p.productId] ?? []).find((d) => d.stockImageId === id);
    if (draft && uploadedIds[id]) return { text: draft.slug.replace(/-u\d+$/, ""), kind: "uploaded" as const };
    if (draft) return { text: draft.slug.replace(/-g\d+(-\d+)?$/, ""), kind: "generated" as const };
    const lib = libraryById[id];
    return { text: lib ? lib.slug : `#${id}`, kind: "manual" as const };
  };
  const LABEL_PREFIX = { alt: "using: ", generated: "generated: ", uploaded: "uploaded: ", manual: "hand-picked: " };

  const generationAvailable = result?.generationAvailable ?? false;
  const generateDisabledTitle = generationAvailable
    ? undefined
    : "Image generation is not configured on this server (OPENAI_API_KEY).";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Match images from the stock library</DialogTitle>
          <DialogDescription>
            {result
              ? <>Scanned {result.totalProducts} products against {result.librarySize} generic
                  produce images.{" "}
                  {result.skippedWithExistingImage > 0 &&
                    `${result.skippedWithExistingImage} already had an image and were left alone.`}
                </>
              : "Scanning the catalogue…"}
          </DialogDescription>
          <label className="flex items-center gap-2 text-xs text-gray-600 pt-1 cursor-pointer select-none">
            <Checkbox
              checked={includeWithImages}
              onCheckedChange={(v) => setIncludeWithImages(v === true)}
              disabled={matchMutation.isPending}
            />
            Also propose for products that already have an image (replaces on apply)
          </label>
        </DialogHeader>

        {matchMutation.isPending && (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw className="h-6 w-6 mx-auto mb-3 animate-spin" />
            Matching products against the library…
          </div>
        )}

        {result && !matchMutation.isPending && (
          // Sizing is flex all the way down, never percentages. The dialog only
          // has a MAX height, so a flex child's flexed size is not "definite"
          // in CSS terms and the viewport's default h-full (100%) silently
          // falls back to its content height — 13,000px for a 122-product
          // catalogue, clipped by the dialog with no way to reach the rows
          // past the fold. Making the viewport a min-h-0 flex-1 item of a flex
          // root lets it shrink to the root's used size instead. Radix's
          // viewport also lays its child out as display:table, which lets the
          // wide picker grid overflow horizontally — force it back to block.
          <ScrollArea className="flex-1 min-h-0 flex flex-col -mx-6 px-6 [&>[data-radix-scroll-area-viewport]]:flex-1 [&>[data-radix-scroll-area-viewport]]:min-h-0 [&>[data-radix-scroll-area-viewport]]:!h-auto [&>[data-radix-scroll-area-viewport]>div]:!block">
            <div className="space-y-6 pb-2 min-w-0">
              {BANDS.map(({ key, label, blurb, Icon, tone }) => {
                const rows = grouped[key];
                if (rows.length === 0) return null;
                const allOn = rows.every((p) => selected[p.productId]);
                return (
                  <section key={key}>
                    <div className={`flex items-start gap-3 rounded-md border px-3 py-2 mb-3 ${tone}`}>
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{label} · {rows.length}</div>
                        <p className="text-xs opacity-80">{blurb}</p>
                      </div>
                      {key !== "NONE" && (
                        <Button
                          size="sm" variant="ghost" className="shrink-0 h-7 text-xs"
                          onClick={() => selectBand(key, !allOn)}
                        >
                          {allOn ? "Deselect all" : "Select all"}
                        </Button>
                      )}
                      {key === "NONE" && (
                        batch ? (
                          <div className="flex items-center gap-2 shrink-0 text-xs">
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            <span className="tabular-nums">{batch.done} of {batch.total}…</span>
                            <Button
                              size="sm" variant="ghost" className="h-7 text-xs"
                              disabled={batchCancel.current}
                              onClick={() => { batchCancel.current = true; }}
                            >
                              {batchCancel.current ? "Stopping…" : "Cancel"}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm" variant="outline" className="shrink-0 h-7 text-xs"
                            disabled={!generationAvailable || atCap}
                            title={generateDisabledTitle ?? (atCap ? `${MAX_CONCURRENT} already generating — wait for one to finish` : undefined)}
                            onClick={() => generateForBand(rows)}
                          >
                            <Sparkles className="h-3.5 w-3.5 mr-1" />Generate for all {rows.length}
                          </Button>
                        )
                      )}
                    </div>

                    <div className="space-y-2">
                      {rows.map((p) => {
                        const current = selected[p.productId] ?? p.stockImageId;
                        const label = chosenLabel(p);
                        return (
                          <div key={p.productId} className="rounded-md border bg-white">
                            <div className="flex items-center gap-3 p-2">
                              <Checkbox
                                checked={!!selected[p.productId]}
                                disabled={!current}
                                onCheckedChange={() => choose(p.productId, current)}
                                aria-label={`Apply image to ${p.productName}`}
                              />
                              {current ? (
                                <img
                                  src={thumbUrl(current)}
                                  alt=""
                                  className="h-14 w-14 rounded object-cover border bg-gray-50 shrink-0"
                                />
                              ) : (
                                <div className="h-14 w-14 rounded border bg-gray-50 shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm truncate flex items-center gap-2">
                                  {p.productName}
                                  {label && (
                                    <Badge
                                      variant="secondary"
                                      className={`text-[10px] font-normal ${label.kind === "generated" ? "bg-violet-50 text-violet-700" : label.kind === "uploaded" ? "bg-sky-50 text-sky-700" : ""}`}
                                    >
                                      {LABEL_PREFIX[label.kind]}{label.text}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 truncate">{p.reason}</div>
                                {p.alternatives.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {p.alternatives.slice(0, 4).map((alt) => (
                                      <button
                                        key={alt.stockImageId}
                                        onClick={() => choose(p.productId, alt.stockImageId)}
                                        className={`text-[11px] rounded border px-1.5 py-0.5 hover:bg-gray-100 ${
                                          selected[p.productId] === alt.stockImageId
                                            ? "border-blue-400 bg-blue-50 text-blue-700"
                                            : "border-gray-200 text-gray-600"
                                        }`}
                                        title="Use this image instead"
                                      >
                                        {alt.slug}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {p.score > 0 && (
                                <Badge variant="outline" className="shrink-0 text-xs tabular-nums">
                                  {p.score}
                                </Badge>
                              )}
                              {selected[p.productId] && isDraftId(selected[p.productId]) && (
                                <label
                                  className="flex items-center gap-1.5 text-[11px] text-gray-600 shrink-0 cursor-pointer select-none"
                                  title="Ticked: this generated or uploaded image joins the shared library once applied. Unticked: it goes on this product only."
                                >
                                  <Checkbox
                                    className="h-3.5 w-3.5"
                                    checked={keepInLibrary[p.productId] !== false}
                                    onCheckedChange={(v) =>
                                      setKeepInLibrary((prev) => ({ ...prev, [p.productId]: v === true }))}
                                  />
                                  Keep in library
                                </label>
                              )}
                              <Button
                                size="sm"
                                variant={genPanels[p.productId] ? "secondary" : key === "NONE" ? "default" : "ghost"}
                                className="h-7 text-xs shrink-0"
                                disabled={!generationAvailable && !genPanels[p.productId]}
                                title={generateDisabledTitle}
                                onClick={() => { togglePanel(p.productId); setBrowsing(null); }}
                              >
                                {generating[p.productId]
                                  ? <><RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />Generating…</>
                                  : <><Sparkles className="h-3.5 w-3.5 mr-1" />Generate
                                      {!genPanels[p.productId] && (drafts[p.productId]?.length ?? 0) > 0 && (
                                        <span className="ml-1 rounded bg-violet-100 text-violet-700 px-1 tabular-nums">
                                          {drafts[p.productId].length}
                                        </span>
                                      )}
                                    </>}
                              </Button>
                              <input
                                ref={(el) => { fileInputs.current[p.productId] = el; }}
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = "";
                                  if (f) uploadFor(p, f);
                                }}
                              />
                              <Button
                                size="sm" variant="ghost" className="h-7 text-xs shrink-0"
                                disabled={!!uploading[p.productId]}
                                title="Upload your own image for this product (png/jpg/gif/webp, max 5MB). Reviewed and applied like any other pick."
                                onClick={() => fileInputs.current[p.productId]?.click()}
                              >
                                {uploading[p.productId]
                                  ? <><RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />Uploading…</>
                                  : <><Upload className="h-3.5 w-3.5 mr-1" />Upload</>}
                              </Button>
                              <Button
                                size="sm" variant={browsing === p.productId ? "secondary" : "ghost"}
                                className="h-7 text-xs shrink-0"
                                onClick={() => {
                                  setBrowsing(browsing === p.productId ? null : p.productId);
                                  // Browse replaces this row's Generate panel only; other rows keep cooking.
                                  setGenPanels((prev) => { const next = { ...prev }; delete next[p.productId]; return next; });
                                }}
                              >
                                <Search className="h-3.5 w-3.5 mr-1" />Browse
                              </Button>
                            </div>
                            {browsing === p.productId && (
                              <LibraryPicker
                                library={library.data ?? []}
                                loading={library.isLoading}
                                current={selected[p.productId] ?? null}
                                initialQuery={p.productName}
                                onPick={(id) => { choose(p.productId, id); setBrowsing(null); }}
                                onClose={() => setBrowsing(null)}
                              />
                            )}
                            {genPanels[p.productId] && (
                              <GeneratePanel
                                subject={subjectFor(p)}
                                onSubjectChange={(v) => setSubjects((prev) => ({ ...prev, [p.productId]: v }))}
                                drafts={drafts[p.productId] ?? []}
                                current={selected[p.productId] ?? null}
                                busy={!!generating[p.productId]}
                                atCap={atCap}
                                model={result?.generationModel ?? null}
                                onGenerate={() => generateFor(p, subjectFor(p))}
                                onPick={(id) => choose(p.productId, id)}
                                onClose={() => togglePanel(p.productId)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="border-t pt-4">
          <div className="mr-auto text-sm text-gray-600">
            {selectedCount} of {result?.proposals.length ?? 0} selected
            {inflightCount > 0 && (
              <span className="ml-2 text-xs text-violet-700">
                · {inflightCount} generating{atCap ? " (max)" : ""}
              </span>
            )}
          </div>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button
            disabled={selectedCount === 0 || applyMutation.isPending || inflightCount > 0 || Object.keys(uploading).length > 0}
            onClick={() =>
              applyMutation.mutate(
                Object.entries(selected).map(([productId, stockImageId]) => ({
                  productId: Number(productId),
                  stockImageId,
                  ...(isDraftId(stockImageId)
                    ? { keepInLibrary: keepInLibrary[Number(productId)] !== false }
                    : {}),
                })),
              )
            }
          >
            {applyMutation.isPending ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Applying…</>
            ) : (
              <><Images className="h-4 w-4 mr-2" />Apply {selectedCount} image{selectedCount === 1 ? "" : "s"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline generation panel under a row: an editable subject line, a Generate
 * button, honest progress copy (Sunburst is the slow, precise variant), and
 * the drafts so far in the same grid style as LibraryPicker. Previous drafts
 * stay visible across "Try again" until the panel closes.
 */
function GeneratePanel({
  subject, onSubjectChange, drafts, current, busy, atCap, model, onGenerate, onPick, onClose,
}: {
  subject: string;
  onSubjectChange: (v: string) => void;
  drafts: Draft[];
  current: number | null;
  busy: boolean;
  atCap: boolean;
  model: string | null;
  onGenerate: () => void;
  onPick: (id: number) => void;
  onClose: () => void;
}) {
  const canGenerate = subject.trim().length >= 3 && !busy && !atCap;
  return (
    <div className="border-t bg-violet-50/40 p-2">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-violet-500 shrink-0" />
        <Input
          autoFocus
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && canGenerate) onGenerate(); }}
          placeholder="e.g. a bunch of arugula · a 10 lb mesh bag of red onions"
          className="h-8 text-sm bg-white"
          disabled={busy}
        />
        <Button
          size="sm" className="h-8 text-xs shrink-0" disabled={!canGenerate} onClick={onGenerate}
          title={!busy && atCap ? `${MAX_CONCURRENT} already generating — wait for one to finish` : undefined}
        >
          {busy ? (
            <><RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />Generating…</>
          ) : (
            <>{drafts.length > 0 ? "Try again" : GENERATE_VARIANTS > 1 ? `Generate ${GENERATE_VARIANTS}` : "Generate"}</>
          )}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-[11px] text-gray-500 px-1 mb-2">
        The subject is the only thing that changes between images; the house style (white
        seamless, soft shadow, square) is fixed and matched to four library references.
        {busy && (
          <> Usually about 20 seconds{model ? ` on ${model}` : ""} — you can move on to other rows meanwhile.</>
        )}
      </p>
      {drafts.length > 0 && (
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
          {drafts.map((d, i) => (
            <button
              key={d.stockImageId}
              onClick={() => onPick(d.stockImageId)}
              title={`Draft ${i + 1} — click to use`}
              className={`rounded border overflow-hidden bg-white hover:ring-2 hover:ring-violet-300 text-left ${
                current === d.stockImageId ? "ring-2 ring-violet-500" : ""
              }`}
            >
              <img src={thumbUrl(d.stockImageId)} alt="" className="w-full aspect-square object-cover" loading="lazy" />
              <div className="text-[10px] leading-tight px-1 py-0.5 truncate text-gray-700">draft {i + 1}</div>
            </button>
          ))}
        </div>
      )}
      {drafts.length === 0 && !busy && (
        <div className="text-[11px] text-gray-500 px-1">
          Edit the subject so it names the exact thing — variety, pack, count — then generate.
          Nothing is written to a product until you tick the row and Apply.
        </div>
      )}
    </div>
  );
}

/** Searchable thumbnail grid over the whole produce library, inline under a row. */
function LibraryPicker({
  library, loading, current, initialQuery, onPick, onClose,
}: {
  library: StockImageMeta[];
  loading: boolean;
  current: number | null;
  initialQuery: string;
  onPick: (id: number) => void;
  onClose: () => void;
}) {
  // Seed the search with the product's first word — usually the produce noun —
  // so the grid opens near the right neighbourhood instead of at "apples".
  const [q, setQ] = useState(() => initialQuery.replace(/[^a-zA-Z ]+/g, " ").trim().split(/\s+/)
    .find((w) => w.length > 3 && !/^\d/.test(w)) ?? "");
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hits = library.filter((s) => {
    const hay = `${s.slug} ${s.name} ${s.category}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  }).slice(0, 60);

  return (
    <div className="border-t bg-gray-50 p-2">
      <div className="flex items-center gap-2 mb-2">
        <Search className="h-4 w-4 text-gray-400 shrink-0" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the library — e.g. roma, bushel, 50lb"
          className="h-8 text-sm"
        />
        <span className="text-xs text-gray-500 shrink-0">
          {loading ? "loading…" : `${hits.length}${hits.length === 60 ? "+" : ""} of ${library.length}`}
        </span>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>
      {!loading && hits.length === 0 && (
        <div className="text-xs text-gray-500 px-1 py-3">Nothing in the library matches that.</div>
      )}
      <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
        {hits.map((s) => (
          <button
            key={s.id}
            onClick={() => onPick(s.id)}
            title={`${s.slug} (${s.category})${s.verified ? "" : " — name unconfirmed"}`}
            className={`rounded border overflow-hidden bg-white hover:ring-2 hover:ring-blue-300 text-left ${
              current === s.id ? "ring-2 ring-blue-500" : ""
            }`}
          >
            <img src={thumbUrl(s.id)} alt="" className="w-full aspect-square object-cover" loading="lazy" />
            <div className="text-[10px] leading-tight px-1 py-0.5 truncate text-gray-700">{s.slug}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

async function superAdminGet(url: string) {
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 401) throw new Error("Session expired - please log in again");
  if (!res.ok) throw new Error("Request failed");
  return res.json();
}

async function superAdminUpload(url: string, body: FormData) {
  const res = await fetch(url, { method: "POST", credentials: "include", body });
  if (res.status === 401) throw new Error("Session expired - please log in again");
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Upload failed");
  }
  return res.json();
}

async function superAdminPost(url: string, body: unknown) {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 401) throw new Error("Session expired - please log in again");
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Request failed");
  }
  return res.json();
}
